// src/services/notificationService.js
import { authService } from './authService';
import { getUserDetails } from "../utils/helpers";

class NotificationService {
    constructor() {
        this.showToast = null;
    }

    /**
     * Get the authenticated axios instance
     */
    getApi() {
        return authService.getApi();
    }

    /**
     * Set the toast function from useToast hook
     */
    setToastFunction(toastFunction) {
        this.showToast = toastFunction;
    }

    /**
     * Handle API errors consistently
     */
    handleError(error, defaultMessage = 'Operation failed') {
        let message = defaultMessage;
        let statusCode = null;
        let errors = null;

        if (error.response) {
            statusCode = error.response.status;
            const responseData = error.response.data || {};
            message = responseData.message || defaultMessage;
            errors = responseData.errors || null;

            switch (statusCode) {
                case 401:
                    message = message || 'Unauthorized access';
                    break;
                case 403:
                    message = message || 'Access forbidden';
                    break;
                case 404:
                    message = message || 'Resource not found';
                    break;
                case 422:
                    if (errors) {
                        const errorMessages = Object.values(errors)
                            .flat()
                            .map(err => `• ${err}`)
                            .join('\n');
                        message = `${responseData.message || 'Validation failed'}:\n${errorMessages}`;
                    }
                    break;
                case 500:
                    message = message || 'Server error occurred';
                    break;
                default:
                    message = message || `Error ${statusCode}`;
            }
        } else if (error.request) {
            message = 'Network error. Please check your connection.';
        } else {
            message = error.message || defaultMessage;
        }

        if (this.showToast) {
            this.showToast("Error", message, "error");
        } else {
            console.warn('Toast function not set. Message:', message);
        }

        return {
            success: false,
            message,
            statusCode,
            errors
        };
    }

    /**
     * Get all notifications with pagination and filters
     * @param {string} endpoint - The API endpoint
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    async getAll(endpoint, params = {}) {
        try {
            const api = this.getApi();
            const response = await api.get(endpoint, { params });
            const responseData = response.data;

            if (responseData.success === true || responseData.status === 'success') {
                return {
                    success: true,
                    data: responseData.data || [],
                    pagination: responseData.pagination || null,
                    unread_count: responseData.unread_count || 0,
                    message: responseData.message || 'Notifications fetched successfully'
                };
            } else {
                const errorMessage = responseData.message || 'Failed to fetch notifications';
                if (this.showToast) {
                    this.showToast("Error", errorMessage, "error");
                }
                return {
                    success: false,
                    data: [],
                    message: errorMessage,
                    unread_count: 0
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to fetch notifications');
        }
    }

    /**
     * Get recent notifications
     * @param {string} endpoint - The API endpoint for recent notifications
     * @param {Object} params - Parameters (limit, etc.)
     * @returns {Promise<Object>}
     */
    async getRecent(endpoint, params = {}) {
        try {
            const api = this.getApi();
            const response = await api.get(endpoint, { params });
            const responseData = response.data;

            if (responseData.success === true || responseData.status === 'success') {
                return {
                    success: true,
                    data: responseData.data || [],
                    unread_count: responseData.unread_count || 0,
                    message: responseData.message || 'Recent notifications fetched successfully'
                };
            } else {
                const errorMessage = responseData.message || 'Failed to fetch recent notifications';
                if (this.showToast) {
                    this.showToast("Error", errorMessage, "error");
                }
                return {
                    success: false,
                    data: [],
                    message: errorMessage,
                    unread_count: 0
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to fetch recent notifications');
        }
    }

    /**
     * Get notification statistics
     * @param {string} endpoint - The API endpoint for statistics
     * @param {Object} params - Parameters
     * @returns {Promise<Object>}
     */
    async getStatistics(endpoint, params = {}) {
        try {
            const api = this.getApi();
            const response = await api.get(endpoint, { params });
            const responseData = response.data;

            if (responseData.success === true || responseData.status === 'success') {
                return {
                    success: true,
                    data: responseData.data || {},
                    message: responseData.message || 'Statistics fetched successfully'
                };
            } else {
                const errorMessage = responseData.message || 'Failed to fetch statistics';
                if (this.showToast) {
                    this.showToast("Error", errorMessage, "error");
                }
                return {
                    success: false,
                    data: null,
                    message: errorMessage
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to fetch statistics');
        }
    }

    /**
     * Mark notification as read
     * @param {string} endpoint - The API endpoint for marking as read
     * @param {Object} data - Optional data
     * @returns {Promise<Object>}
     */
    async markAsRead(endpoint, data = {}) {
        try {
            const api = this.getApi();
            const response = await api.put(endpoint, data);
            const responseData = response.data;

            if (responseData.success === true || responseData.status === 'success') {
                return {
                    success: true,
                    data: responseData.data,
                    message: responseData.message || 'Notification marked as read'
                };
            } else {
                const errorMessage = responseData.message || 'Failed to mark notification as read';
                if (this.showToast) {
                    this.showToast("Error", errorMessage, "error");
                }
                return {
                    success: false,
                    message: errorMessage
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to mark notification as read');
        }
    }

    /**
     * Mark all notifications as read
     * @param {string} endpoint - The API endpoint for marking all as read
     * @param {Object} data - Data containing user identifier
     * @returns {Promise<Object>}
     */
    async markAllAsRead(endpoint, data = {}) {
        try {
            const api = this.getApi();
            const response = await api.put(endpoint, data);
            const responseData = response.data;

            if (responseData.success === true || responseData.status === 'success') {
                return {
                    success: true,
                    message: responseData.message || 'All notifications marked as read'
                };
            } else {
                const errorMessage = responseData.message || 'Failed to mark all notifications as read';
                if (this.showToast) {
                    this.showToast("Error", errorMessage, "error");
                }
                return {
                    success: false,
                    message: errorMessage
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to mark all notifications as read');
        }
    }

    /**
     * Delete a notification
     * @param {string} endpoint - The API endpoint for deletion
     * @returns {Promise<Object>}
     */
    async delete(endpoint) {
        try {
            const api = this.getApi();
            const response = await api.delete(endpoint);
            const responseData = response.data;

            if (responseData.success === true || responseData.status === 'success') {
                return {
                    success: true,
                    message: responseData.message || 'Notification deleted successfully'
                };
            } else {
                const errorMessage = responseData.message || 'Failed to delete notification';
                if (this.showToast) {
                    this.showToast("Error", errorMessage, "error");
                }
                return {
                    success: false,
                    message: errorMessage
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to delete notification');
        }
    }

    /**
     * Get single notification by ID
     * @param {string} endpoint - The API endpoint with ID
     * @returns {Promise<Object>}
     */
    async getById(endpoint) {
        try {
            const api = this.getApi();
            const response = await api.get(endpoint);
            const responseData = response.data;

            if (responseData.success === true || responseData.status === 'success') {
                return {
                    success: true,
                    data: responseData.data,
                    message: responseData.message || 'Notification fetched successfully'
                };
            } else {
                const errorMessage = responseData.message || 'Failed to fetch notification';
                if (this.showToast) {
                    this.showToast("Error", errorMessage, "error");
                }
                return {
                    success: false,
                    data: null,
                    message: errorMessage
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to fetch notification');
        }
    }

    /**
     * Bulk delete notifications
     * @param {string} endpoint - The API endpoint for bulk deletion
     * @param {Object} data - Data containing array of IDs
     * @returns {Promise<Object>}
     */
    async bulkDelete(endpoint, data = {}) {
        try {
            const api = this.getApi();
            const response = await api.post(endpoint, data);
            const responseData = response.data;

            if (responseData.success === true || responseData.status === 'success') {
                return {
                    success: true,
                    message: responseData.message || 'Notifications deleted successfully'
                };
            } else {
                const errorMessage = responseData.message || 'Failed to delete notifications';
                if (this.showToast) {
                    this.showToast("Error", errorMessage, "error");
                }
                return {
                    success: false,
                    message: errorMessage
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to delete notifications');
        }
    }
}

export const notificationService = new NotificationService();